"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "http://localhost:8000";

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function VendorAuth() {
  const router = useRouter();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // LOGIN
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // REGISTER
  const [name, setName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [storeName, setStoreName] = useState("");
  const [storeDescription, setStoreDescription] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [photo, setPhoto] = useState(null);

  // For preview
  const [photoUrl, setPhotoUrl] = useState(null);

  const resetErrors = () => setErrors({});

  async function handleLogin(e) {
    e.preventDefault();
    resetErrors();
    const errs = {};
    if (!validateEmail(loginEmail)) errs.loginEmail = "Email tidak valid";
    if (!loginPassword) errs.loginPassword = "Password wajib diisi";
    if (Object.keys(errs).length) return setErrors(errs);

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/auth/vendor/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "Login gagal");

      localStorage.setItem("access_token", body.access_token);
      localStorage.setItem("vendor", JSON.stringify(body.vendor));
      // Route by role
      if (body.vendor.role === "admin") {
        router.push("/admin/dashboard");
      } else {
        router.push("/vendor/products");
      }
    } catch (err) {
      setErrors({ form: err.message });
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    resetErrors();
    const errs = {};
    if (!name.trim()) errs.name = "Nama wajib diisi";
    if (!validateEmail(regEmail)) errs.regEmail = "Email tidak valid";
    if (!phone.trim()) errs.phone = "Nomor telepon wajib diisi";
    if (!storeName.trim()) errs.storeName = "Nama toko wajib diisi";
    if (!storeDescription.trim())
      errs.storeDescription = "Deskripsi toko wajib diisi";
    if (!password) errs.password = "Password wajib diisi";
    if (password !== password2) errs.password2 = "Konfirmasi password tidak sama";
    if (Object.keys(errs).length) return setErrors(errs);

    try {
      setLoading(true);
      const form = new FormData();
      form.append("name", name);
      form.append("email", regEmail);
      form.append("password", password);
      form.append("storeName", storeName);
      form.append("phone", phone);
      form.append("storeDescription", storeDescription);
      if (photo) form.append("photo", photo);

      const res = await fetch(`${API_BASE}/auth/vendor/register`, {
        method: "POST",
        body: form,
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "Registrasi gagal");

      alert("Registrasi sukses! Silakan login.");
      setMode("login");
      // reset form
      setName(""); setRegEmail(""); setPhone("");
      setStoreName(""); setStoreDescription("");
      setPassword(""); setPassword2(""); setPhoto(null); setPhotoUrl(null);
    } catch (err) {
      setErrors({ form: err.message });
    } finally {
      setLoading(false);
    }
  }

  function handlePhoto(e) {
    const file = e.target.files[0];
    setPhoto(file);
    if (file) {
      setPhotoUrl(URL.createObjectURL(file));
    } else {
      setPhotoUrl(null);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow p-6">
        <h1 className="text-2xl font-semibold text-center mb-6">
          {mode === "login" ? "Vendor Login" : "Vendor Registration"}
        </h1>

        {errors.form && (
          <div className="mb-4 text-sm text-red-600">{errors.form}</div>
        )}

        {mode === "login" ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                className={`w-full border rounded px-3 py-2 text-sm ${errors.loginEmail ? "border-red-500" : "border-gray-300"}`}
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="you@example.com"
              />
              {errors.loginEmail && (
                <p className="text-xs text-red-500 mt-1">{errors.loginEmail}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password</label>
              <input
                className={`w-full border rounded px-3 py-2 text-sm ${errors.loginPassword ? "border-red-500" : "border-gray-300"}`}
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="********"
              />
              {errors.loginPassword && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.loginPassword}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Loading..." : "Login"}
            </button>

            <p className="text-xs text-center mt-4">
              Belum punya akun?{" "}
              <button
                type="button"
                className="text-blue-600 underline"
                onClick={() => setMode("register")}
              >
                Register
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-4" encType="multipart/form-data">
            <div>
              <label className="block text-sm font-medium mb-1">Nama</label>
              <input
                className={`w-full border rounded px-3 py-2 text-sm ${errors.name ? "border-red-500" : "border-gray-300"}`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nama lengkap"
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                className={`w-full border rounded px-3 py-2 text-sm ${errors.regEmail ? "border-red-500" : "border-gray-300"}`}
                type="email"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                placeholder="you@example.com"
              />
              {errors.regEmail && (
                <p className="text-xs text-red-500 mt-1">{errors.regEmail}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Nomor Telepon</label>
              <input
                className={`w-full border rounded px-3 py-2 text-sm ${errors.phone ? "border-red-500" : "border-gray-300"}`}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+62xxxxxxxxxxx"
              />
              {errors.phone && (
                <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Nama Toko</label>
              <input
                className={`w-full border rounded px-3 py-2 text-sm ${errors.storeName ? "border-red-500" : "border-gray-300"}`}
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                placeholder="Nama toko anda"
              />
              {errors.storeName && (
                <p className="text-xs text-red-500 mt-1">{errors.storeName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Deskripsi Toko</label>
              <textarea
                rows={3}
                className={`w-full border rounded px-3 py-2 text-sm ${errors.storeDescription ? "border-red-500" : "border-gray-300"}`}
                value={storeDescription}
                onChange={(e) => setStoreDescription(e.target.value)}
                placeholder="Ceritakan tentang toko Anda"
              />
              {errors.storeDescription && (
                <p className="text-xs text-red-500 mt-1">{errors.storeDescription}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Foto (opsional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhoto}
                className="w-full border rounded px-3 py-2 text-sm"
              />
              {photoUrl && (
                <img
                  src={photoUrl}
                  alt="Preview"
                  className="mt-2 mb-0 rounded border w-24 h-24 object-cover"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Password</label>
              <input
                className={`w-full border rounded px-3 py-2 text-sm ${errors.password ? "border-red-500" : "border-gray-300"}`}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="********"
              />
              {errors.password && (
                <p className="text-xs text-red-500 mt-1">{errors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Konfirmasi Password</label>
              <input
                className={`w-full border rounded px-3 py-2 text-sm ${errors.password2 ? "border-red-500" : "border-gray-300"}`}
                type="password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                placeholder="********"
              />
              {errors.password2 && (
                <p className="text-xs text-red-500 mt-1">{errors.password2}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 rounded bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? "Loading..." : "Register"}
            </button>

            <p className="text-xs text-center mt-4">
              Sudah punya akun?{" "}
              <button
                type="button"
                className="text-blue-600 underline"
                onClick={() => setMode("login")}
              >
                Login
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
